// Raw card inputs for Razorpay Custom Checkout. Extracted from
// razorpay-custom-recharge-modal so that modal stays readable once the saved-card
// path is added alongside it (repo convention: split components > 150 lines).
//
// These fields collect the PAN/CVV in OUR OWN DOM — that is what makes Custom
// Checkout PCI-DSS SAQ A-EP rather than SAQ A. PRIVATE_FIELD_CLASS + data-private
// keep both session recorders (PostHog and LogRocket) from ever recording them.

import { cn } from '@neo/lib/utils'
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { PRIVATE_FIELD_CLASS } from 'v2/configs/constants'
import {
  CVV_MAX_LENGTH,
  clampNumericInput,
  EXPIRY_PART_LENGTH,
  formatExpiry,
  parseExpiryInput,
  type RazorpayCustomCardFieldValues,
} from './razorpay-card-entry'

// Match the shared inputs: Inter 14px/400 with 22px leading. Labels inherit
// the shared 13px/500 style with 20px leading, including when they wrap.
const FIELD_TEXT_CLASS = 'text-p-400'

// Card fields only. The save-card consent stays recordable — it is not PCI data,
// and the class is a blockClass so marking the wrapper would blank the whole block
// in replay rather than mask the value.
//
// Goes on `inputClassName`, not `className`: neo's Input spreads `className` onto
// BOTH the label/input wrapper and the input itself, and PRIVATE_FIELD_CLASS is a
// blockClass — on the wrapper it would blank the label along with the value.
const cardPrivateProps = {
  inputClassName: cn(PRIVATE_FIELD_CLASS, FIELD_TEXT_CLASS),
  'data-private': true,
} as const

interface RazorpayCustomCardFieldsProps {
  values: RazorpayCustomCardFieldValues
  onChange: (field: keyof RazorpayCustomCardFieldValues, value: string) => void
  disabled?: boolean
  // Save-card consent. Omitting both hides the control entirely. Every current
  // caller passes them, including the add-card entry point — note that leaving the
  // box unchecked there charges the customer without vaulting the card.
  saveCard?: boolean
  onSaveCardChange?: (checked: boolean) => void
}

export const RazorpayCustomCardFields = ({
  values,
  onChange,
  disabled = false,
  saveCard,
  onSaveCardChange,
}: RazorpayCustomCardFieldsProps) => (
  // Labelled fields on the same two-column grid the Add Card wizard uses, so the
  // one screen in the console where a PAN is typed reads like the rest of the
  // product rather than like a raw gateway form.
  //
  // Field order follows the card: number, then the expiry and CVV that sit under
  // it, then the holder's name — the one thing not read straight off the front,
  // and the one people pause over, so it goes last rather than interrupting.
  //
  // Email and contact are NOT here. createPayment requires both, but the console
  // already holds them (see the modal's prefill), so asking again would be asking
  // the customer to retype what we know.
  //
  // No "Card details" heading either: the dialog title already says a card is
  // being added, and every field carries its own label.
  <div className="grid grid-cols-2 gap-4">
    <Input
      className="col-span-2"
      label="Card Number"
      name="razorpay-custom-card-number"
      // The grouping itself is the hint — it shows the shape expected without a
      // sentence, and matches how the number is printed.
      placeholder="xxxx xxxx xxxx xxxx"
      value={values.cardNumber}
      onChange={(e) => onChange('cardNumber', e.target.value)}
      disabled={disabled}
      data-testid="razorpay-custom-card-number"
      autoComplete="off"
      inputMode="numeric"
      {...cardPrivateProps}
    />
    {/* ONE box for the expiry, as it is printed on the card and as customers type
        it. parseExpiryInput does the split Razorpay needs; a single change emits
        both halves so they can never disagree. */}
    <Input
      className="col-span-1"
      label="Expiry"
      name="razorpay-custom-expiry"
      placeholder="MM / YY"
      value={formatExpiry(values.expMonth, values.expYear)}
      onChange={(e) => {
        const { month, year } = parseExpiryInput(e.target.value)
        onChange('expMonth', month)
        onChange('expYear', year)
      }}
      disabled={disabled}
      data-testid="razorpay-custom-expiry"
      inputMode="numeric"
      // Four digits plus the " / " the formatter inserts.
      maxLength={EXPIRY_PART_LENGTH * 2 + 3}
      {...cardPrivateProps}
    />
    <Input
      className="col-span-1"
      label="CVC/CVV"
      name="razorpay-custom-cvv"
      type="password"
      placeholder="CVV"
      value={values.cvv}
      onChange={(e) => onChange('cvv', clampNumericInput(e.target.value, CVV_MAX_LENGTH))}
      disabled={disabled}
      data-testid="razorpay-custom-cvv"
      inputMode="numeric"
      autoComplete="off"
      maxLength={CVV_MAX_LENGTH}
      {...cardPrivateProps}
    />
    <Input
      className="col-span-2"
      label="Card Holder Name"
      name="razorpay-custom-card-name"
      placeholder="Enter card holder name"
      value={values.cardName}
      onChange={(e) => onChange('cardName', e.target.value)}
      disabled={disabled}
      data-testid="razorpay-custom-card-name"
      autoComplete="off"
      {...cardPrivateProps}
    />
    {onSaveCardChange && (
      /* Checked by default (see the host's `saveCard` state for why), but still a
         checkbox: Razorpay only vaults when we ask (save_card on the order +
         save:1 on the payment), so unchecking genuinely charges without storing.
         Not marked private — this carries no card data. */
      <div className="col-span-2">
        <Checkbox
          id="razorpay-custom-save-card"
          name="razorpay-custom-save-card"
          label="Save this card for future payments"
          value={!!saveCard}
          onChange={onSaveCardChange}
          disabled={disabled}
          data-testid="razorpay-custom-save-card"
        />
      </div>
    )}
  </div>
)
